import {
    Flex,
    MenuButtonTrigger,
    MenuItemButton,
    ToggleButton,
    Tooltip,
} from "@amzn/storm-ui";
import { isEmpty } from "lodash";
import { useState, type FunctionComponent } from "react";
import { useStore } from "zustand";

import {
    FilterContainer,
    StyledMenu,
    StyledPill,
    VerticalLine,
} from "../../components/discovery/filters/discoveryFilterSharedStyles";
import { GeneratedInsightsViewer } from "../../components/discovery/insights/generatedInsights/GeneratedInsightsViewer";
import { PitchedContentsViewer } from "../../components/discovery/insights/pitchedContents/PitchedContentsViewer";
import { ACCOUNT_AGNOSTIC_UNAVAILABLE } from "../../config/constants";
import { TELEMETRY_ID } from "../../constants/telemetry";
import { navigationStore } from "../../store/navigation";
import { yourInsightFilterStore } from "../../store/yourInsightsFilter";
import type { YourInsightFilterContentSubType } from "../../types/filters/yourInsights";
import {
    BaseInsightFilterContentType,
    YourInsightFilterContentType,
    YourInsightFilterTypeMappings,
    defaultYourInsightFilterState,
} from "../../types/filters/yourInsights";
import { insightTypeLabelLookup } from "../../utils/generatedInsights.utils";
import { isAccountAgnosticId } from "../../utils/permission.utils";
import { removeWhitespace } from "../../utils/string.utils";
import { logWithTakt } from "../../utils/telemetry.utils";

import { DiscoveryBody } from "./DiscoveryBody";
import { YourInsightsTypeTooltip } from "./YourInsightsTypeTooltip";

const SwitchContentViewer: FunctionComponent<{
    contentType: YourInsightFilterContentType;
}> = ({ contentType }) => {
    switch (contentType) {
        case YourInsightFilterContentType.PITCHED_CONTENT:
            return <PitchedContentsViewer />;
        case YourInsightFilterContentType.INSIGHTS:
            return <GeneratedInsightsViewer />;
    }
};

export const YourInsights: React.FunctionComponent<{
    hasPlmAccess?: boolean;
}> = ({ hasPlmAccess = false }) => {
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [insightIdentifier, setIdentifier] = useStore(
        navigationStore,
        (state) => [state.identifier, state.setIdentifier],
    );

    const [contentType, contentSubType, setTypes] = useStore(
        yourInsightFilterStore,
        (state) => [state.contentType, state.contentSubType, state.setTypes],
    );

    const clearSingleIdentifierView = () => {
        setIdentifier(undefined);
    };

    const onMenuItemClick = (
        updateContentSubType: YourInsightFilterContentSubType,
    ): void => {
        setTypes(contentType, updateContentSubType);
        setIsMenuOpen(false);
    };

    const toggleMenu = (): void => {
        setIsMenuOpen(!isMenuOpen);
    };

    const toggleContentType = (
        updateContentType: YourInsightFilterContentType,
    ): void => {
        const updateContentSubType =
            defaultYourInsightFilterState[updateContentType].contentSubType;
        setTypes(updateContentType, updateContentSubType);
        logWithTakt(TELEMETRY_ID.YOUR_INSIGHTS_CONTENT_TYPE_CHANGE, {
            contentType: updateContentType,
        });
    };

    const disableContentTypeConfig = {
        [YourInsightFilterContentType.PITCHED_CONTENT]: {
            condition: () => isAccountAgnosticId(),
            message: ACCOUNT_AGNOSTIC_UNAVAILABLE,
        },
    };

    return (
        <>
            <FilterContainer>
                <Flex
                    alignItems="center"
                    rowGap="xsmall"
                    columnGap="xsmall"
                    wrap="wrap"
                >
                    {insightIdentifier ? (
                        <StyledPill
                            data-testid="your-insights-identifier-pill"
                            prefix="identifier"
                            label={insightIdentifier}
                            onClose={clearSingleIdentifierView}
                        />
                    ) : (
                        <>
                            {Object.values(
                                hasPlmAccess
                                    ? YourInsightFilterContentType
                                    : BaseInsightFilterContentType,
                            ).map((type) => {
                                const config = disableContentTypeConfig[type];
                                const isDisabled = config?.condition() ?? false;
                                const message = config?.message ?? "";
                                return (
                                    <Tooltip
                                        nonInteractive={!isDisabled}
                                        inert={!isDisabled}
                                        message={message}
                                        key={removeWhitespace(message)}
                                        trigger={
                                            <span>
                                                <ToggleButton
                                                    key={removeWhitespace(type)}
                                                    disabled={isDisabled}
                                                    active={
                                                        contentType === type
                                                    }
                                                    type="button"
                                                    data-testid={`toggle-content-${removeWhitespace(type)}`}
                                                    onClick={() =>
                                                        toggleContentType(type)
                                                    }
                                                >
                                                    {type}
                                                </ToggleButton>
                                            </span>
                                        }
                                    />
                                );
                            })}

                            {!isEmpty(
                                YourInsightFilterTypeMappings[contentType],
                            ) && (
                                <>
                                    <VerticalLine />
                                    <StyledMenu
                                        id={`${removeWhitespace(contentType)}-Menu`}
                                        data-testid={`${removeWhitespace(contentType)}-Menu-Test`}
                                        onClick={toggleMenu}
                                        onClose={toggleMenu}
                                        isOpen={isMenuOpen}
                                        trigger={
                                            <MenuButtonTrigger
                                                data-testid={`${removeWhitespace(contentType)}-pill-Test`}
                                            >
                                                <b>Type:</b>{" "}
                                                {
                                                    insightTypeLabelLookup[
                                                        contentSubType!
                                                    ]
                                                }
                                            </MenuButtonTrigger>
                                        }
                                    >
                                        {YourInsightFilterTypeMappings[
                                            contentType
                                        ].map((type) => {
                                            return (
                                                <MenuItemButton
                                                    key={`${removeWhitespace(type)}`}
                                                    data-testid={`${removeWhitespace(type)}-option`}
                                                    onClick={() =>
                                                        onMenuItemClick(type)
                                                    }
                                                >
                                                    {
                                                        insightTypeLabelLookup[
                                                            type
                                                        ]
                                                    }
                                                </MenuItemButton>
                                            );
                                        })}
                                    </StyledMenu>
                                    <YourInsightsTypeTooltip />
                                </>
                            )}
                        </>
                    )}
                </Flex>
            </FilterContainer>
            <DiscoveryBody data-testid="discovery-body">
                <SwitchContentViewer contentType={contentType} />
            </DiscoveryBody>
        </>
    );
};
